import { useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { EventPlace } from 'helpers/analytics';
import { AgentSerializer } from 'services/api/agent/v3/agent-serializer';
import { trackEvent } from 'services/event-tracking';
import { getLoggedInAgentRole } from 'store/entities/agents/selectors';
import { getLicenseId, getPlanType } from 'store/features/session/selectors';

interface TrackingProperties {
  role: string;
  plan: string;
  hit: number;
  place: string;
  licenseNumber: number | null;
}

interface UseVisitorLimitExceededTrackingResult {
  trackDisplayed: () => void;
  trackClicked: () => void;
  trackSkipped: () => void;
  trackTooltipDisplayed: () => void;
}

function getPlaceFromPathname(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);

  return segments[0] || 'unknown';
}

export function useVisitorLimitExceededTracking(
  count: number,
  isVisible: boolean,
): UseVisitorLimitExceededTrackingResult {
  const permission = useSelector(getLoggedInAgentRole);
  const planType = useSelector(getPlanType);
  const licenseNumber = useSelector(getLicenseId);
  const { pathname } = useLocation();
  const hasTrackedDisplayRef = useRef(false);
  const lastTooltipDisplayAtRef = useRef(0);

  const getTrackingProperties = useCallback((): TrackingProperties => {
    const role = permission ? AgentSerializer.permissionToRoleMap(permission) : 'unknown';
    const plan = planType ?? 'unknown';
    const place = getPlaceFromPathname(pathname);

    return {
      role,
      plan,
      hit: count,
      place,
      licenseNumber,
    };
  }, [permission, planType, count, pathname, licenseNumber]);

  const trackDisplayed = useCallback((): void => {
    trackEvent('Visitor limit banner displayed', EventPlace.TopBar, getTrackingProperties());
  }, [getTrackingProperties]);

  const trackClicked = useCallback((): void => {
    trackEvent('Visitor limit banner action clicked', EventPlace.TopBar, getTrackingProperties());
  }, [getTrackingProperties]);

  const trackSkipped = useCallback((): void => {
    trackEvent('Visitor limit banner action skipped', EventPlace.TopBar, getTrackingProperties());
  }, [getTrackingProperties]);

  const trackTooltipDisplayed = useCallback((): void => {
    const now = Date.now();
    const tooltipDisplayCooldownMs = 3000;

    if (now - lastTooltipDisplayAtRef.current < tooltipDisplayCooldownMs) {
      return;
    }

    lastTooltipDisplayAtRef.current = now;
    trackEvent('Visitor limit tooltip displayed', EventPlace.TopBar, getTrackingProperties());
  }, [getTrackingProperties]);

  useEffect(() => {
    if (isVisible && !hasTrackedDisplayRef.current) {
      trackDisplayed();
      hasTrackedDisplayRef.current = true;
    }
  }, [isVisible, trackDisplayed]);

  return {
    trackDisplayed,
    trackClicked,
    trackSkipped,
    trackTooltipDisplayed,
  };
}
